import { QueryModelsByMake, SchemaTypes } from '@allesauto/graphql';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import { initializeApollo } from '@/global/apollo/apollo';
/* code defines a Redux slice named pcp/filters/makeModel that 
   manages the state for the make and model filters in a dropdown */
const SLICE_NAME = 'pcp/filters/makeModel';

export interface MakeModelFilterData {
  makes: {
    selected: string | undefined | null;
  };
  models: {
    loading: boolean;
    data: SchemaTypes.Model[];
    selected: string | undefined;
  };
}

export interface MakeModelFilterState {
  data: MakeModelFilterData[];
  filterCount: number;
}

const filterData: MakeModelFilterData = {
  makes: {
    selected: undefined,
  },
  models: {
    loading: false,
    data: [],
    selected: undefined,
  },
};

export const initialState: MakeModelFilterState = {
  data: [cloneDeep(filterData)],
  filterCount: 0,
};

/* 
 asynchronous thunk that fetches the 
 models for a given make name using Apollo Client 
 before dispatching regular Redux actions
*/
export const fetchModelsByMake = createAsyncThunk(
  `${SLICE_NAME}/fetchModelsByMake`,
  async (props: { makeName: string; filterIndex: number }) => {
    const apolloClient = initializeApollo();

    const { data } = await apolloClient.query<{
      models: SchemaTypes.Model[];
    }>({
      query: QueryModelsByMake,
      variables: {
        makeName: props.makeName,
      },
    });
    return data?.models || [];
  }
);
/* generates the reducer, action creators, and action types for the slice */
const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setMake(
      state,
      action: PayloadAction<{ makeName: string; filterIndex: number }>
    ) {
      if (!state.data[action.payload.filterIndex]) {
        state.data.push(cloneDeep(filterData));
      }

      state.data[action.payload.filterIndex].makes.selected =
        action.payload.makeName;

      state.filterCount = state.data.filter((fd) => !!fd.makes.selected).length;
    },

    setModel(
      state,
      action: PayloadAction<{ modelName: string; filterIndex: number }>
    ) {
      if (!state.data[action.payload.filterIndex]) {
        state.data.push(cloneDeep(filterData));
      }

      state.data[action.payload.filterIndex].models.selected =
        action.payload.modelName;
    },

    addFilter(state) {
      state.data.push(cloneDeep(filterData));
    },

    removeFilter(
      state,
      action: PayloadAction<{
        filterIndex: number;
      }>
    ) {
      if (action.payload.filterIndex === 0 && state.data.length === 1) {
        state.data = [cloneDeep(filterData)];
        state.filterCount = 0;
      } else {
        state.data.splice(action.payload.filterIndex, 1);
        state.filterCount = state.data.filter(
          (fd) => fd.makes.selected !== undefined
        ).length;
      }
    },

    reset() {
      return initialState;
    },
  },
  /* handles the pending, fulfilled, and rejected states of the fetchModelsByMake action, 
  updating the loading status and model data in the state. */
  extraReducers: (builder) => {
    builder.addCase(fetchModelsByMake.pending, (state, action) => {
      const { filterIndex } = action.meta.arg;

      state.data[filterIndex].models.loading = true;
      state.data[filterIndex].models.data = [];
    });

    builder.addCase(fetchModelsByMake.fulfilled, (state, action) => {
      const { filterIndex } = action.meta.arg;

      state.data[filterIndex].models.loading = false;
      state.data[filterIndex].models.data = action.payload;
    });

    builder.addCase(fetchModelsByMake.rejected, (state, action) => {
      const { filterIndex } = action.meta.arg;
      state.data[filterIndex].models.loading = false;
    });
  },
});

const selectPCPFilterMakeModelState = (state: {
  pages: { pcp: { filters: { makeModel: MakeModelFilterState } } };
}) => state.pages.pcp.filters.makeModel;

export const selectPCPFilterMakeModelModels = createSelector(
  selectPCPFilterMakeModelState,
  ({ data }) => data.map(({ models }) => models.data)
);

export const selectPCPFilterMakeModelSelectedMakes = createSelector(
  selectPCPFilterMakeModelState,
  ({ data }) => data.map(({ makes }) => makes.selected)
);

// action creators generated by createSlice
export const { setMake, setModel, addFilter, removeFilter, reset } =
  slice.actions;
export default slice.reducer;
